"use client";
import React, { useEffect, useState } from "react";
import { getLeadDealers, LeadDealer, submitLeadDealers } from "@/app/utilities/apis";

interface LeadDealerStepProps {
    leadId: string;
    carName?: string;
    // Called once the step is finished — dealers saved, or none found nearby
    onDone: () => void;
}

// Shown after a lead is confirmed (post-OTP, or straight after submit when OTP
// is off) for models with dealer_selection_enabled. Lists the nearest dealers
// to the lead's location and lets the user pick one or more.
const LeadDealerStep: React.FC<LeadDealerStepProps> = ({ leadId, carName, onDone }) => {
    const [dealers, setDealers] = useState<LeadDealer[] | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        getLeadDealers(leadId).then((list) => {
            if (cancelled) return;
            // Nothing nearby (or lookup failed) — the lead is already complete
            if (!list.length) onDone();
            else setDealers(list);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadId]);

    const toggleDealer = (id: string) => {
        setError("");
        setSelected((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));
    };

    const handleSubmit = async () => {
        if (!selected.length || isSubmitting) return;
        setIsSubmitting(true);
        const res = await submitLeadDealers(leadId, selected);
        setIsSubmitting(false);
        if (res?.status === "success") onDone();
        else setError("Something went wrong. Please try again.");
    };

    if (!dealers) {
        return (
            <div className="flex flex-col items-center justify-center py-10 gap-3" aria-live="polite">
                <span className="w-8 h-8 border-4 border-[#008C8B]/30 border-t-[#008C8B] rounded-full animate-spin" />
                <p className="text-[14px] text-[#6B7280]">Finding dealers near you...</p>
            </div>
        );
    }

    const countLabel = `${dealers.length} ${carName ? `${carName} ` : ""}dealer${dealers.length > 1 ? "s" : ""} near you`;

    return (
        <div>
            <p className="font-roboto font-semibold text-[16px] text-[#1F2937]">
                Select Dealer<span className="text-red-500">*</span>
            </p>
            <p className="font-roboto text-[14px] text-[#6B7280] mt-1 mb-3">{countLabel}</p>

            <ul className="max-h-[280px] overflow-y-auto pr-1" role="group" aria-label="Dealers">
                {dealers.map((dealer) => {
                    const isChecked = selected.includes(dealer._id);
                    return (
                        <li key={dealer._id} className="border-b border-gray-200 last:border-b-0">
                            <label className={`flex items-center justify-between gap-3 px-2 py-4 cursor-pointer rounded-md transition-colors ${isChecked ? "bg-[#008C8B]/5" : "hover:bg-gray-50"}`}>
                                <span className="min-w-0">
                                    <span className="block text-[15px] text-[#1F2937] truncate">{dealer.name}</span>
                                    <span className="block text-[12px] text-[#90a1b9] mt-0.5">{dealer.distance_km} km away</span>
                                </span>
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={isChecked}
                                    onChange={() => toggleDealer(dealer._id)}
                                />
                                <span
                                    aria-hidden="true"
                                    className={`shrink-0 w-6 h-6 rounded-md border flex items-center justify-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#008C8B]/50 ${isChecked ? "bg-[#008C8B] border-[#008C8B]" : "bg-white border-gray-300"}`}
                                >
                                    {isChecked && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    )}
                                </span>
                            </label>
                        </li>
                    );
                })}
            </ul>

            {error && <div className="text-red-500 text-sm mt-2 text-center">{error}</div>}

            {selected.length > 0 && (
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`w-full bg-[#008C8B] text-white py-3 rounded-md mt-4 font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 ${isSubmitting ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                    data-testid="lead-dealer-submit"
                >
                    {isSubmitting ? "Submitting..." : "Submit"}
                </button>
            )}
        </div>
    );
};

export default LeadDealerStep;
